/**
 * HistoryPage — /history route (Stage 08 [08.6])
 *
 * Figma: node 319:5771
 * Acceptance §2.4
 *
 * Real backend: GET /api/history
 *   type   = 'all'|'generations'|'photo'|'video'|'payment'|'credit'|'referral'
 *   period = 'all'|'7d'|'30d'|'1y'
 *   cursor = opaque base64 (passed as-is, never parsed)
 *   limit  = 1–50 (default 20)
 *
 * Layout (per Figma screenshot):
 *   - Dark bg (bg-bg-app)
 *   - Header «Операции» centered
 *   - HistoryFilterBar (type + period dropdowns)
 *   - Optional date group labels (grouped by local date)
 *   - HistoryRecord rows (no card, no border — gap only)
 *   - «Загрузить ещё» button for pagination (no infinite scroll — safer for mobile)
 *   - Empty state: «Нет операций за этот период» + «Сбросить фильтры»
 *   - Error state: «Не удалось загрузить историю»
 *
 * Design tokens only — no hex literals, no hand-crafted SVG.
 * Back navigation: Telegram native BackButton (show on mount, hide on unmount).
 */

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UIButton } from '../components/primitives';
import HistoryFilterBar from '../components/history/HistoryFilterBar';
import HistoryRecord from '../components/history/HistoryRecord';
import { useHistory } from '../hooks/useHistory';
import { formatHistoryDate } from '../utils/dateFormat';
import { getPlatform } from '../platform';
import { useConsentStore } from '../store/consentStore';

// ---------------------------------------------------------------------------
// Date group helpers
// ---------------------------------------------------------------------------

/** Format ISO date consistently as day month year for group labels. */
function formatGroupLabel(isoString) {
  return formatHistoryDate(isoString);
}

/** Stable date key «YYYY-MM-DD» for grouping */
function dateKey(isoString) {
  return isoString ? isoString.slice(0, 10) : '';
}

/**
 * Group items into [{label, items}] by calendar date.
 */
function groupByDate(items) {
  const groups = [];
  let currentKey = null;
  let currentGroup = null;
  for (const item of items) {
    const key = dateKey(item.date);
    if (key !== currentKey) {
      currentKey = key;
      currentGroup = { label: formatGroupLabel(item.date), items: [] };
      groups.push(currentGroup);
    }
    currentGroup.items.push(item);
  }
  return groups;
}

// ---------------------------------------------------------------------------
// HistoryPage
// ---------------------------------------------------------------------------

export function getHistoryTopOffset(
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '',
  telegramPlatform = getPlatform(),
) {
  const normalizedUserAgent = userAgent || '';
  const normalizedTelegramPlatform = telegramPlatform || '';

  const isIOS =
    /iPhone|iPad|iPod/i.test(normalizedUserAgent) ||
    /ios|iphone|ipad/i.test(normalizedTelegramPlatform);

  if (isIOS) return 12;

  const isAndroid =
    /Android/i.test(normalizedUserAgent) ||
    /android/i.test(normalizedTelegramPlatform);

  if (isAndroid) return 60;

  return 62;
}

export default function HistoryPage() {
  const { t } = useTranslation();
  const [type, setType] = useState('all');
  const [period, setPeriod] = useState('all');
  const consentRequired = useConsentStore((state) => state.consentRequired);
  const historyTopClearance = `calc(var(--safe-top, 0px) + ${getHistoryTopOffset()}px)`;

  const { items, isLoading, isLoadingMore, hasMore, error, loadMore, refetch } =
    useHistory({ type, period, enabled: !consentRequired });

  const handleResetFilters = useCallback(() => {
    setType('all');
    setPeriod('all');
  }, []);

  const groups = useMemo(() => groupByDate(items), [items]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div
      data-testid="history-page"
      className="min-h-screen bg-bg-app"
      style={{ paddingTop: historyTopClearance, paddingBottom: '24px' }}
    >
      {/* Header — centered title only. Back navigation is the Telegram
          shell's native BackButton (wired in the useEffect above);
          duplicating it in-app would clutter the header. */}
      <div className="flex items-center justify-center px-4 pb-2">
        <h1 className="font-sans font-bold text-[17px] leading-[20px] text-text-primary uppercase">
          {t('history.operationsTitle')}
        </h1>
      </div>

      {/* Filter bar */}
      <HistoryFilterBar
        type={type}
        period={period}
        onTypeChange={setType}
        onPeriodChange={setPeriod}
      />

      {/* Content */}
      <div className="mt-2">
        {/* Loading skeleton */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <span className="font-sans text-[13px] text-text-secondary">{t('common.loading')}</span>
          </div>
        )}

        {/* Error state */}
        {!isLoading && error && (
          <div className="flex flex-col items-center gap-3 py-12 px-4">
            <p className="font-sans text-[13px] text-text-secondary text-center">
              {t('history.loadError')}
            </p>
            <UIButton type="secondary" size="sm" onClick={refetch}>
              {t('common.retry')}
            </UIButton>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && items.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-12 px-4">
            <p
              data-testid="empty-state-text"
              className="font-sans font-semibold text-[15px] leading-[18px] text-text-secondary text-center"
            >
              {t('history.emptyPeriod')}
            </p>
            <UIButton
              type="secondary"
              size="sm"
              onClick={handleResetFilters}
              data-testid="reset-filters-btn"
            >
              {t('history.resetFilters')}
            </UIButton>
          </div>
        )}

        {/* Grouped list — each HistoryRecord is its own glass card (Figma
            319:5533, surface/4 lvl, radius 16, padding 8). 8px gap between
            cards inside a date group; group label gets its own spacing. */}
        {!isLoading && !error && groups.map((group) => (
          <div key={group.label} className="px-4">
            <div className="pt-4 pb-2">
              <span className="font-sans font-bold text-[14px] leading-none text-text-primary">
                {group.label}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {group.items.map((item) => (
                <HistoryRecord key={item.id} item={item} />
              ))}
            </div>
          </div>
        ))}

        {/* Load more */}
        {!isLoading && hasMore && !error && (
          <div className="flex justify-center pt-4 px-4">
            <UIButton
              type="secondary"
              size="sm"
              onClick={loadMore}
              disabled={isLoadingMore}
              data-testid="load-more-btn"
            >
              {isLoadingMore ? t('common.loading') : t('history.loadMore')}
            </UIButton>
          </div>
        )}
      </div>
    </div>
  );
}
